import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabase';
import type { User } from '../../lib/types';
import type { OrgPilot } from '../../hooks/useOrgPilots';

const BASE_PRIVILEGES = ['PPL', 'CPL', 'ATPL', 'MPL', 'SPL', 'LAPL'] as const;

const identitySchema = z.object({
  email: z
    .string()
    .trim()
    .email('Must be a valid email address')
    .or(z.literal(''))
    .optional(),
  licence_id: z.string().trim().max(100).optional(),
  date_of_birth: z.string().regex(/^$|^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD').optional(),
  nationality: z.string().trim().max(80).optional(),
  base_privileges: z.enum(['', ...BASE_PRIVILEGES]).optional(),
  total_flight_hours: z.string().regex(/^$|^\d{1,5}(\.\d{1,2})?$/, 'Hours must be numeric').optional(),
  total_pic_hours: z.string().regex(/^$|^\d{1,5}(\.\d{1,2})?$/, 'Hours must be numeric').optional(),
  reference_date_flthours: z.string().regex(/^$|^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD').optional(),
});

type IdentityFormValues = z.infer<typeof identitySchema>;

type Target =
  | { kind: 'user'; record: User }
  | { kind: 'org_pilot'; record: OrgPilot };

interface Props {
  target: Target;
  onSaved?: () => void;
}

/**
 * Licence-holder identity tab. Red PII (licence_id, date_of_birth) is edited
 * in the clear here — the server-side guardrail is RLS, not this form.
 */
export default function PilotIdentityForm({ target, onSaved }: Props) {
  const record = target.record as Partial<User & OrgPilot>;
  const table = target.kind === 'user' ? 'users' : 'org_pilots';

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<IdentityFormValues>({
    resolver: zodResolver(identitySchema),
    defaultValues: {
      email: record.email ?? '',
      licence_id: record.licence_id ?? '',
      date_of_birth: record.date_of_birth ?? '',
      nationality: record.nationality ?? '',
      base_privileges: (record.base_privileges ?? '') as IdentityFormValues['base_privileges'],
      total_flight_hours:
        record.total_flight_hours != null ? String(record.total_flight_hours) : '',
      total_pic_hours:
        record.total_pic_hours != null ? String(record.total_pic_hours) : '',
      reference_date_flthours: record.reference_date_flthours ?? '',
    },
  });

  useEffect(() => {
    reset({
      email: record.email ?? '',
      licence_id: record.licence_id ?? '',
      date_of_birth: record.date_of_birth ?? '',
      nationality: record.nationality ?? '',
      base_privileges: (record.base_privileges ?? '') as IdentityFormValues['base_privileges'],
      total_flight_hours:
        record.total_flight_hours != null ? String(record.total_flight_hours) : '',
      total_pic_hours:
        record.total_pic_hours != null ? String(record.total_pic_hours) : '',
      reference_date_flthours: record.reference_date_flthours ?? '',
    });
  }, [record.id, reset]);

  async function onSubmit(values: IdentityFormValues) {
    const cleanEmail = values.email?.trim() ?? '';
    const emailChanged = cleanEmail !== (record.email ?? '');

    // Registered pilots: email goes through the update_user_email RPC (touches
    // auth.users too, which is the login identity). Other identity fields are
    // a direct UPDATE on public.users, gated by the UPDATE policy + is_airline_admin_of.
    // Ad-hoc pilots (org_pilots): email is just a contact field, direct UPDATE.
    if (target.kind === 'user' && emailChanged) {
      if (!cleanEmail) {
        alert('Email is required for registered pilots.');
        return;
      }
      const { error: rpcError } = await supabase.rpc('update_user_email', {
        target_user_id: record.id!,
        new_email: cleanEmail,
      });
      if (rpcError) {
        alert(`Save failed (email): ${rpcError.message}`);
        return;
      }
    }

    const patch: Record<string, unknown> = {
      licence_id: values.licence_id?.trim() || null,
      date_of_birth: values.date_of_birth || null,
      nationality: values.nationality?.trim() || null,
      base_privileges: values.base_privileges || null,
      total_flight_hours: values.total_flight_hours ? Number(values.total_flight_hours) : null,
      total_pic_hours: values.total_pic_hours ? Number(values.total_pic_hours) : null,
      reference_date_flthours: values.reference_date_flthours || null,
    };

    // Ad-hoc pilot email is a plain column on org_pilots (not a login identity);
    // fold it into the same patch.
    if (target.kind === 'org_pilot') {
      patch.email = cleanEmail || null;
    }

    const { error } = await supabase.from(table).update(patch).eq('id', record.id!);
    if (error) {
      alert(`Save failed: ${error.message}`);
      return;
    }
    onSaved?.();
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field
          label="Email"
          error={errors.email?.message}
          hint={target.kind === 'user' ? 'Login email (required)' : 'Optional contact email'}
        >
          <input
            type="email"
            {...register('email')}
            placeholder="name@example.com"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#4A90E2] focus:outline-none"
          />
        </Field>

        <Field label="Licence ID" error={errors.licence_id?.message} redPii>
          <input
            type="text"
            {...register('licence_id')}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#4A90E2] focus:outline-none"
            placeholder="BE-FCL-XXXXXXX"
          />
        </Field>

        <Field label="Date of birth" error={errors.date_of_birth?.message} redPii>
          <input
            type="date"
            {...register('date_of_birth')}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#4A90E2] focus:outline-none"
          />
        </Field>

        <Field label="Nationality" error={errors.nationality?.message}>
          <input
            type="text"
            {...register('nationality')}
            placeholder="BE / Belgian"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#4A90E2] focus:outline-none"
          />
        </Field>

        <Field label="Base privileges" error={errors.base_privileges?.message}>
          <select
            {...register('base_privileges')}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#4A90E2] focus:outline-none"
          >
            <option value="">— not set —</option>
            {BASE_PRIVILEGES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Total flight hours" error={errors.total_flight_hours?.message}>
          <input
            type="text"
            inputMode="decimal"
            {...register('total_flight_hours')}
            placeholder="0.00"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#4A90E2] focus:outline-none"
          />
        </Field>

        <Field label="Total PIC hours" error={errors.total_pic_hours?.message}>
          <input
            type="text"
            inputMode="decimal"
            {...register('total_pic_hours')}
            placeholder="0.00"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#4A90E2] focus:outline-none"
          />
        </Field>

        <Field
          label="Hours reference date"
          hint="As-of date for the total hours above"
          error={errors.reference_date_flthours?.message}
        >
          <input
            type="date"
            {...register('reference_date_flthours')}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#4A90E2] focus:outline-none"
          />
        </Field>
      </div>

      <div className="pt-2 flex items-center gap-3">
        <button
          type="submit"
          disabled={isSubmitting || !isDirty}
          className="rounded bg-[#0B2C45] px-4 py-2 text-sm font-medium text-white hover:bg-[#0B2C45]/90 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving…' : 'Save identity'}
        </button>
        {!isDirty && <span className="text-xs text-gray-500">No changes</span>}
      </div>
    </form>
  );
}

function Field({
  label,
  error,
  hint,
  redPii,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  redPii?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1 flex items-baseline justify-between">
        <span className={`text-sm font-medium ${redPii ? 'text-red-700' : 'text-gray-700'}`}>
          {label}
          {redPii && <span className="ml-1 text-[10px] uppercase tracking-wide">PII</span>}
        </span>
        {hint && <span className="text-xs text-gray-500">{hint}</span>}
      </div>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </label>
  );
}
